import React, { useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const ProjectsCompletedChart = ({ analytics }) => {
  // Default to current month
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  const [startDate, setStartDate] = useState(startOfMonth.toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(endOfMonth.toISOString().slice(0, 10));

  const filteredProjects = useMemo(() => {
    const projects = analytics?.projects || [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    // include the end date fully
    end.setHours(23, 59, 59, 999);
    return projects.filter(p => {
      const status = p?.status || p?.project_status;
      const completedAt = p?.completed_at || p?.updated_at || p?.created_at;
      const completedDate = completedAt ? new Date(completedAt) : null;
      if (!completedDate) return false;
      const isCompleted = status === 'completed' || status === 'concluido' || status === 'finalizado';
      return isCompleted && completedDate >= start && completedDate <= end;
    });
  }, [analytics, startDate, endDate]);

  const chartData = useMemo(() => {
    // Aggregate by day within the selected range
    const start = new Date(startDate);
    const end = new Date(endDate);
    const days = [];
    const map = new Map();
    const cur = new Date(start);
    while (cur <= end) {
      const key = cur.toISOString().slice(0, 10);
      map.set(key, 0);
      days.push(key);
      cur.setDate(cur.getDate() + 1);
    }
    filteredProjects.forEach(p => {
      const d = new Date(p?.completed_at || p?.updated_at || p?.created_at).toISOString().slice(0, 10);
      if (map.has(d)) map.set(d, (map.get(d) || 0) + 1);
    });
    return days.map(d => ({ date: d, total: map.get(d) || 0 }));
  }, [filteredProjects, startDate, endDate]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-popover border border-border rounded-2xl p-3 shadow-elevation-2">
          <p className="text-sm font-medium text-foreground mb-2">{new Date(label).toLocaleDateString('pt-BR')}</p>
          <p className="text-sm text-muted-foreground">Projetos concluídos: {payload[0]?.value}</p>
        </div>
      );
    }
    return null;
  };

  const resetToCurrentMonth = () => {
    setStartDate(startOfMonth.toISOString().slice(0, 10));
    setEndDate(endOfMonth.toISOString().slice(0, 10));
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-foreground">Projetos concluídos no mês</h2>
        <div className="flex items-center space-x-2">
          <div className="flex items-center space-x-2">
            <Icon name="Calendar" size={16} className="text-muted-foreground" />
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            <span className="text-muted-foreground">—</span>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
          <button className="px-3 py-2 text-sm border border-border rounded-md hover:bg-muted/50 transition-colors" onClick={resetToCurrentMonth}>
            Mês atual
          </button>
        </div>
      </div>

      <div className="h-64 w-full" aria-label="Gráfico de projetos concluídos no período">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255, 255, 255, 0.1)" />
            <XAxis 
              dataKey="date" 
              stroke="#94a3b8"
              fontSize={12}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => new Date(value).toLocaleDateString('pt-BR', { day: '2-digit' })}
            />
            <YAxis 
              stroke="#94a3b8"
              fontSize={12}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="total" fill="#00d4ff" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ProjectsCompletedChart;